import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { ShotlistsService } from '@/services/shotlist.service'

export const useFile = (idProps?: string) => {
	const { id: idParams } = useParams()
	const id = idProps || idParams

	const queryClient = useQueryClient()

	const {
		data: shotlist,
		isLoading: isShotlistLoading,
		refetch,
		isFetching,
		isError: isGetError
	} = useQuery({
		queryKey: ['getShotlist', id],
		queryFn: ({ queryKey }) => ShotlistsService.getShotlist(queryKey[1]),
		enabled: !!id
	})

	const { mutateAsync: translateShotlist, isLoading: isTranslateLoading } =
		useMutation({
			mutationKey: ['translateShotlist', id],
			mutationFn: ({
				target_language,
				source_language
			}: {
				target_language: string
				source_language?: string
			}) => {
				if (!id || !shotlist) throw new Error('Missing task ID or shotlist')

				const actualSourceLang = source_language || shotlist.original.language

				if (!actualSourceLang) throw new Error('Source language is missing')

				return ShotlistsService.translateShotlist(
					id,
					target_language,
					actualSourceLang
				)
			},
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['getShotlist', id] })
			}
		})

	const { mutateAsync: updateShotlist, isLoading: isUpdateLoading } =
		useMutation({
			mutationKey: ['updateShotlist'],
			mutationFn: ({
				content,
				translationId
			}: {
				content: string
				translationId: number
			}) => ShotlistsService.updateShotlist(id, content, translationId),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['getShotlist', id] })
			}
		})

	const { mutateAsync: deleteTranslation, isLoading: isDeleteLoading } =
		useMutation({
			mutationKey: ['deleteShotlistTranslation'],
			mutationFn: (translationId: number) =>
				ShotlistsService.deleteShotlistTranslation(id, translationId),
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['getShotlist', id] })
			}
		})

	return {
		shotlist,
		isShotlistLoading,
		refetch,
		isFetching,
		isGetError,
		translateShotlist,
		isTranslateLoading,
		updateShotlist,
		isUpdateLoading,
		deleteTranslation,
		isDeleteLoading
	}
}
